import client, { transformKeys } from './client';

import type {
  BulkCreateLinearResponse,
  CreateLinearIssueRequest,
  CreateLinearIssueResponse,
  DuplicateCheckResult,
  FeatureRequest,
  LinearLabel,
  LinearTeam,
  ListUploadsResponse,
  ProcessTranscriptRequest,
  ProcessTranscriptResponse,
  UpdateFeatureRequestRequest,
  UpdateUploadRequest,
  UploadSummary,
} from '../types/featureRequests';

/**
 * Process a transcript and extract feature requests/bugs using AI.
 */
export async function processTranscript(
  request: ProcessTranscriptRequest
): Promise<ProcessTranscriptResponse> {
  const response = await client.post('/feature-requests/process', {
    transcript: request.transcript,
    source: request.source,
    source_metadata: request.sourceMetadata,
    caller_org_id: request.callerOrgId,
    caller_org_name: request.callerOrgName,
    caller_user_id: request.callerUserId,
    caller_user_name: request.callerUserName,
    call_date: request.callDate,
  });
  return transformKeys(response.data) as ProcessTranscriptResponse;
}

/**
 * List all transcript uploads for the current user.
 */
export async function listUploads(limit: number = 50): Promise<ListUploadsResponse> {
  const response = await client.get('/feature-requests/uploads', {
    params: { limit },
  });
  return transformKeys(response.data) as ListUploadsResponse;
}

/**
 * Get details of a specific upload.
 */
export async function getUpload(uploadId: string): Promise<UploadSummary> {
  const response = await client.get(`/feature-requests/uploads/${uploadId}`);
  return transformKeys(response.data) as UploadSummary;
}

/**
 * Get all extracted features/bugs for an upload.
 */
export async function getUploadFeatures(uploadId: string): Promise<FeatureRequest[]> {
  const response = await client.get(`/feature-requests/uploads/${uploadId}/features`);
  return transformKeys(response.data) as FeatureRequest[];
}

/**
 * Get the original transcript text for an upload.
 */
export async function getUploadTranscript(uploadId: string): Promise<string> {
  const response = await client.get(`/feature-requests/uploads/${uploadId}/transcript`);
  return response.data.transcript;
}

/**
 * Update upload metadata (meeting details).
 */
export async function updateUpload(
  uploadId: string,
  updates: UpdateUploadRequest
): Promise<UploadSummary> {
  const response = await client.patch(`/feature-requests/uploads/${uploadId}`, {
    caller_org_id: updates.callerOrgId,
    caller_org_name: updates.callerOrgName,
    caller_user_id: updates.callerUserId,
    caller_user_name: updates.callerUserName,
    call_date: updates.callDate,
  });
  return transformKeys(response.data) as UploadSummary;
}

/**
 * Delete an upload and its feature requests (Linear tickets remain).
 */
export async function deleteUpload(uploadId: string): Promise<void> {
  await client.delete(`/feature-requests/uploads/${uploadId}`);
}

/**
 * Update a feature request before creating a Linear ticket.
 */
export async function updateFeatureRequest(
  featureId: string,
  updates: UpdateFeatureRequestRequest
): Promise<FeatureRequest> {
  const response = await client.patch(`/feature-requests/features/${featureId}`, {
    title: updates.title,
    description: updates.description,
    priority: updates.priority,
    item_type: updates.itemType,
    tags: updates.tags,
  });
  return transformKeys(response.data) as FeatureRequest;
}

/**
 * Check for potential duplicate Linear issues for a feature request.
 */
export async function checkDuplicates(featureId: string): Promise<DuplicateCheckResult> {
  const response = await client.get(
    `/feature-requests/features/${featureId}/check-duplicates`
  );
  return transformKeys(response.data) as DuplicateCheckResult;
}

/**
 * Create a Linear issue from a feature request.
 */
export async function createLinearIssue(
  request: CreateLinearIssueRequest
): Promise<CreateLinearIssueResponse> {
  const response = await client.post('/feature-requests/linear/create', {
    feature_request_id: request.featureRequestId,
    team_id: request.teamId,
    project_id: request.projectId,
    labels: request.labels,
    assignee_id: request.assigneeId,
    skip_duplicate_check: request.skipDuplicateCheck,
  });
  return transformKeys(response.data) as CreateLinearIssueResponse;
}

/**
 * Create multiple Linear issues from feature requests.
 */
export async function bulkCreateLinearIssues(
  featureRequestIds: string[],
  teamId?: string,
  projectId?: string,
  skipDuplicateCheck: boolean = false
): Promise<BulkCreateLinearResponse> {
  const response = await client.post('/feature-requests/linear/bulk-create', {
    feature_request_ids: featureRequestIds,
    team_id: teamId,
    project_id: projectId,
    skip_duplicate_check: skipDuplicateCheck,
  });
  return transformKeys(response.data) as BulkCreateLinearResponse;
}

/**
 * Get available Linear teams.
 */
export async function getLinearTeams(): Promise<LinearTeam[]> {
  const response = await client.get('/feature-requests/linear/teams');
  return transformKeys(response.data) as LinearTeam[];
}

/**
 * Get available Linear labels for a team.
 */
export async function getLinearLabels(teamId?: string): Promise<LinearLabel[]> {
  const response = await client.get('/feature-requests/linear/labels', {
    params: teamId ? { team_id: teamId } : {},
  });
  return transformKeys(response.data) as LinearLabel[];
}
